import { Lightbulb, Wallet, Users, Footprints, ArrowRight, CalendarDays, Lock, MessageCircle, BookOpen, Check } from "lucide-react";
import { rs } from "@/pages/landing/parts";
import { catLabel } from "@/lib/ab";

const Cta = ({ onClick, children, testid, light }) => (
  <button onClick={onClick} className={`lp-btn ${light ? "lp-btn-light" : ""} w-full sm:w-auto min-h-[56px] px-7 text-[16px]`} data-testid={testid}>{children} <ArrowRight className="h-5 w-5" /></button>
);
const H2 = ({ children, light }) => <h2 className={`font-display font-extrabold text-2xl sm:text-3xl leading-tight ${light ? "text-white" : "text-[#102B3F]"}`}>{children}</h2>;

export function FHero({ t, cta, img }) {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-5 pb-8 lg:py-14 grid lg:grid-cols-2 gap-6 lg:gap-12 items-center" data-testid="free-hero">
      <div>
        <h1 className="font-display font-extrabold text-[30px] leading-[1.1] sm:text-5xl lg:text-[54px] text-[#102B3F]" data-testid="free-hero-title">{t.hero.h1}</h1>
        <p className="text-[17px] sm:text-xl text-[#102B3F] font-semibold mt-3 leading-snug">{t.hero.sub}</p>
        <p className="text-[15px] sm:text-lg text-[#4A5D6A] mt-2 leading-relaxed">{t.hero.sub2}</p>
        <div className="mt-5"><Cta onClick={() => cta("hero")} testid="free-hero-cta">{t.hero.cta}</Cta></div>
        <p className="text-[13px] font-semibold text-[#088F7E] mt-2.5 text-center sm:text-left" data-testid="free-hero-note">{t.hero.note}</p>
      </div>
      {img ? <img src={img} alt={t.hero.alt} width="1264" height="848" fetchpriority="high" className="w-full aspect-[3/2] object-cover rounded-3xl shadow-[0_24px_60px_-30px_rgba(16,43,63,.55)]" data-testid="free-hero-image" /> : <div className="w-full aspect-[3/2] rounded-3xl bg-[#EAF8F4]" />}
    </section>
  );
}

export function FProblem({ t }) {
  const p = t.problem;
  return (
    <section className="bg-[#102B3F] text-white py-10 sm:py-14" data-testid="free-problem-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <H2 light>{p.title}</H2>
        <ul className="mt-5 space-y-3">{p.lines.map((l) => <li key={l} className="flex gap-3 text-[16px] text-white/85"><span className="h-2 w-2 rounded-full bg-[#0BAF9A] mt-2.5 shrink-0" />{l}</li>)}</ul>
        <p className="mt-6 font-display font-bold text-lg">{p.close}</p>
        <p className="mt-1 text-[16px] text-[#5FE0CB]">{p.help}</p>
      </div>
    </section>
  );
}

export function FGet({ t, cta }) {
  const icons = [Lightbulb, Wallet, Users, Footprints];
  return (
    <section className="bg-[#F4F8F9] py-10 sm:py-14" data-testid="free-get-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <H2>{t.get.title}</H2>
        <p className="text-[16px] text-[#4A5D6A] mt-2">{t.get.sub}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          {t.get.cards.map(([title, d], i) => {
            const Icon = icons[i];
            return (
              <div key={title} className="rounded-2xl bg-white border border-[#E1ECEF] p-5 flex sm:block gap-4 items-start" data-testid={`free-get-card-${i}`}>
                <span className="relative h-12 w-12 rounded-2xl bg-[#EAF8F4] flex items-center justify-center shrink-0"><Icon className="h-6 w-6 text-[#088F7E]" /><span className="absolute -top-1.5 -left-1.5 h-5 w-5 rounded-full bg-[#102B3F] text-white text-[11px] font-bold flex items-center justify-center">{i + 1}</span></span>
                <div><div className="font-display font-bold text-lg leading-snug text-[#102B3F] sm:mt-3">{title}</div><p className="text-[15px] text-[#5C6B73] mt-0.5">{d}</p></div>
              </div>
            );
          })}
        </div>
        <div className="mt-6"><Cta onClick={() => cta("get")} testid="free-get-cta">{t.get.cta}</Cta></div>
      </div>
    </section>
  );
}

export function FPreview({ t, cta, img }) {
  const p = t.preview;
  const icons = [Wallet, CalendarDays, Users, BookOpen];
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="free-preview-section">
      <H2>{p.title}</H2>
      <div className="mt-5 rounded-3xl border-2 border-[#0BAF9A] bg-white overflow-hidden grid lg:grid-cols-[1fr_1.15fr]">
        {img ? <img src={img} alt={p.alt} loading="lazy" width="1024" height="1024" className="w-full h-56 sm:h-72 lg:h-full object-cover" data-testid="free-preview-image" /> : <div className="w-full h-56 sm:h-72 lg:h-full bg-[#EAF8F4]" />}
        <div className="p-5 sm:p-7">
          <h3 className="font-display font-extrabold text-3xl text-[#102B3F]" data-testid="free-preview-name">{p.name}</h3>
          <p className="text-[15px] text-[#36495A] mt-2 leading-relaxed">{p.desc}</p>
          <div className="grid gap-2 mt-4">
            {p.facts.map(([l, v], i) => { const Icon = icons[i]; return <div key={l} className="flex items-start gap-3 rounded-xl bg-[#F4F8F9] px-3 py-2.5"><Icon className="h-5 w-5 text-[#088F7E] shrink-0 mt-0.5" /><div className="text-sm"><span className="text-[#7D8C96] font-semibold">{l} </span><b className="text-[#102B3F]">{v}</b></div></div>; })}
          </div>
          <p className="mt-4 flex gap-2 text-[15px] font-semibold text-[#102B3F]"><Check className="h-5 w-5 text-[#0BAF9A] shrink-0" strokeWidth={3} />{p.steps}</p>
          <div className="mt-5"><Cta onClick={() => cta("preview")} testid="free-preview-cta">{p.cta}</Cta></div>
          <p className="text-[13px] font-semibold text-[#088F7E] mt-2.5 text-center sm:text-left">{p.note}</p>
        </div>
      </div>
    </section>
  );
}

export function FMore({ t, ideas, offer, unlock, lang }) {
  const m = t.more;
  return (
    <section className="bg-[#F4F8F9] py-10 sm:py-14" data-testid="free-more-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <H2>{m.title}</H2>
        <div className="mt-3 space-y-1 text-[16px] text-[#4A5D6A] max-w-2xl">{m.lines.map((l) => <p key={l}>{l}</p>)}</div>
        <p className="mt-3 text-[16px] font-semibold text-[#102B3F] max-w-2xl">{m.text}</p>
        {m.sub && <p className="text-[16px] text-[#4A5D6A] max-w-2xl">{m.sub}</p>}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-5">
          {ideas.map((i) => (
            <div key={i.number} className="rounded-2xl bg-white border border-[#E1ECEF] p-4" data-testid={`free-more-idea-${i.number}`}>
              <div className="text-[11px] font-semibold text-[#7D8C96]">{catLabel(i.category, lang)}</div>
              <div className="font-display font-bold text-[#102B3F] leading-tight mt-1">{i.name}</div>
              <div className="mt-2 text-xs text-[#5C6B73]">{m.start} <b className="text-[#102B3F]">{i.startup_cost}</b></div>
              <Lock className="h-4 w-4 text-[#0BAF9A] mt-2" />
            </div>
          ))}
        </div>
        <p className="mt-5 text-[15px] text-[#36495A] max-w-2xl">{m.close}</p>
        <button onClick={unlock} className="mt-3 w-full sm:w-auto min-h-[52px] px-6 rounded-2xl border-2 border-[#102B3F] text-[#102B3F] font-bold inline-flex items-center justify-center gap-2" data-testid="free-more-unlock-btn"><span>{m.cta}{offer && <> — <span className="whitespace-nowrap">{rs(offer.price)}</span></>}</span> <ArrowRight className="h-4 w-4" /></button>
      </div>
    </section>
  );
}

export function FTrust({ t, trust }) {
  const r = t.trust;
  return (
    <section className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="free-trust-section">
      <H2>{r.title}</H2>
      <div className="mt-4 space-y-2 text-[16px] text-[#36495A]">{r.paras.map((x) => <p key={x}>{x}</p>)}</div>
      <p className="mt-4 font-display font-bold text-[#102B3F]" data-testid="free-trust-no">{r.no}</p>
      {trust?.company_name && <p className="mt-4 text-sm text-[#5C6B73]" data-testid="free-trust-operator">{r.operator.replace("{company}", trust.company_name)}</p>}
      {trust?.whatsapp_number && (
        <a href={`https://wa.me/${trust.whatsapp_number}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 min-h-[44px] text-sm font-bold text-[#088F7E]" data-testid="free-trust-whatsapp"><MessageCircle className="h-4 w-4" />{r.wa}</a>
      )}
    </section>
  );
}

export function FFinal({ t, cta }) {
  return (
    <section className="bg-[#102B3F] text-white" data-testid="free-final-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16 text-center">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl leading-tight max-w-2xl mx-auto">{t.final.h}</h2>
        <p className="mt-3 text-[16px] text-white/80 max-w-xl mx-auto">{t.final.p1} {t.final.p2}</p>
        <div className="mt-6"><Cta onClick={() => cta("final")} testid="free-final-cta" light>{t.final.cta}</Cta></div>
        <p className="text-[13px] font-semibold text-[#5FE0CB] mt-2.5">{t.final.note}</p>
      </div>
    </section>
  );
}
